import { useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import JobForm from "../components/JobForm";
import JobPage from "../components/JobPage";
import Dashboard from "../components/Dashboard";
import CalendarView from "../components/CalendarView";
import AttendanceForm from "../components/AttendanceForm";
import ProfileEditor from "../components/ProfileEditor";
import ReportingDashboard from "../components/ReportingDashboard";
import { useAppContext } from "../context/AppContext";
import { uploadJobAttachment } from "../utils/fileUpload";
import logger from "../utils/logger";

export function PostJobPage({ entities, tiers }) {
  const { currentUser, showSuccess, showError, addEntry, agencyId,
    createJob, createRecurringJobs, updateJob,
  } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const fromRequest = location.state?.fromRequest || null;
  const cloneFrom = location.state?.cloneFrom || null;

  const uploadFilesToJob = useCallback(async (jobId, files) => {
    if (!files || files.length === 0) return;
    const attachments: any[] = [];
    for (const file of files) {
      const attachment = await uploadJobAttachment(jobId, file);
      attachments.push(attachment);
    }
    await updateJob(jobId, { attachments });
  }, [updateJob]);

  const handlePostJob = useCallback(async (form, recurrence, files) => {
    try {
      if (!currentUser) return;
      if (recurrence?.recurring && recurrence.dates.length > 0) {
        const seriesId = `series_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const { ids, errors } = await createRecurringJobs({ ...form, agencyId }, currentUser.id, recurrence.dates, seriesId);
        if (ids.length === 0) {
          throw new Error("All recurring job creations failed.");
        }
        if (files && files.length > 0) {
          for (const jobId of ids) {
            try { await uploadFilesToJob(jobId, files); } catch (e) { logger.error("Attachment upload failed for job", jobId, e); }
          }
        }
        await addEntry(currentUser, "recurring_jobs_created", ids[0], form.title,
          `Created ${ids.length} of ${recurrence.dates.length} recurring jobs (${recurrence.frequency})`);
        if (errors.length > 0) {
          showError(`${ids.length} of ${recurrence.dates.length} jobs created. ${errors.length} failed.`);
        } else {
          showSuccess(`${ids.length} recurring jobs posted successfully.`);
        }
      } else {
        const jobId = await createJob({ ...form, agencyId }, currentUser.id);
        await uploadFilesToJob(jobId, files);
        await addEntry(currentUser, "job_created", jobId, form.title, "Job created");
        // If this job was created from a request, mark the request as converted
        if (fromRequest?.requestId) {
          try {
            const { updateJobRequest } = await import("../hooks/useJobRequests");
            const { serverTimestamp } = await import("firebase/firestore");
            await updateJobRequest(fromRequest.requestId, {
              status: "converted",
              convertedJobId: jobId,
              reviewedBy: currentUser.id,
              reviewedAt: serverTimestamp(),
            });
            // If an invoice exists for this request, link the job to it
            if (fromRequest.invoiceId) {
              const { updateInvoice } = await import("../hooks/useInvoices");
              await updateInvoice(fromRequest.invoiceId, { jobId });
            }
          } catch (e) {
            logger.error("Failed to update request status:", e);
          }
        }
        showSuccess("Job posted successfully.");
      }
      navigate("/");
    } catch (err) {
      showError("Failed to post job: " + err.message);
      throw err;
    }
  }, [createJob, createRecurringJobs, uploadFilesToJob, agencyId, currentUser, addEntry, showSuccess, showError, navigate, fromRequest]);

  return (
    <div className="page-container">
      <JobForm
        entities={entities}
        tiers={tiers}
        initialData={fromRequest || cloneFrom}
        onSubmit={handlePostJob}
        onCancel={() => navigate("/")}
      />
    </div>
  );
}

// This component is rendered inline by App.js, which owns the edit flow and
// updateJob handler. Kept as a named export so existing imports don't break.
export function EditJobPage() {
  return null;
}

export function DashboardPage({ jobs, users, currentUser, jobsLoading }) {
  const { showSuccess, showError,
    signup, withdraw, joinWaitlist, leaveWaitlist, setEditingJob,
  } = useAppContext();

  const handleSignup = useCallback(async (jobId) => {
    try {
      await signup(jobId);
      showSuccess("Signed up for job.");
    } catch (err) {
      showError("Failed to sign up: " + err.message);
    }
  }, [signup, showSuccess, showError]);

  const handleWithdraw = useCallback(async (jobId) => {
    try {
      await withdraw(jobId);
      showSuccess("Withdrew from job.");
    } catch (err) {
      showError("Failed to withdraw: " + err.message);
    }
  }, [withdraw, showSuccess, showError]);

  const handleJoinWaitlist = useCallback(async (jobId) => {
    try {
      await joinWaitlist(jobId);
      showSuccess("Joined waitlist.");
    } catch (err) {
      showError("Failed to join waitlist: " + err.message);
    }
  }, [joinWaitlist, showSuccess, showError]);

  const handleLeaveWaitlist = useCallback(async (jobId) => {
    try {
      await leaveWaitlist(jobId);
      showSuccess("Left waitlist.");
    } catch (err) {
      showError("Failed to leave waitlist: " + err.message);
    }
  }, [leaveWaitlist, showSuccess, showError]);

  return (
    <Dashboard
      jobs={jobs}
      users={users}
      currentUser={currentUser}
      loading={jobsLoading}
      onSignup={handleSignup}
      onWithdraw={handleWithdraw}
      onJoinWaitlist={handleJoinWaitlist}
      onLeaveWaitlist={handleLeaveWaitlist}
      onEdit={(job) => setEditingJob(job)}
    />
  );
}



export function CalendarPage({ jobs, currentUser }) {
  const { showSuccess, showError, signup, withdraw } = useAppContext();

  const handleSignup = useCallback(async (jobId) => {
    try {
      await signup(jobId);
      showSuccess("Signed up for job.");
    } catch (err) {
      showError("Failed to sign up: " + err.message);
    }
  }, [signup, showSuccess, showError]);

  const handleWithdraw = useCallback(async (jobId) => {
    try {
      await withdraw(jobId);
      showSuccess("Withdrew from job.");
    } catch (err) {
      showError("Failed to withdraw: " + err.message);
    }
  }, [withdraw, showSuccess, showError]);

  return (
    <div className="page-container">
      <CalendarView
        jobs={jobs}
        currentUser={currentUser}
        onSignup={handleSignup}
        onWithdraw={handleWithdraw}
      />
    </div>
  );
}

export function JobDetailPage({ jobs, currentUser, jobsLoading }) {
  const { showSuccess, showError, signup, withdraw, joinWaitlist, leaveWaitlist } = useAppContext();

  const handleSignup = useCallback(async (jobId) => {
    try {
      await signup(jobId);
      showSuccess("Signed up for job.");
    } catch (err) {
      showError("Failed to sign up: " + err.message);
    }
  }, [signup, showSuccess, showError]);

  const handleWithdraw = useCallback(async (jobId) => {
    try {
      await withdraw(jobId);
      showSuccess("Withdrew from job.");
    } catch (err) {
      showError("Failed to withdraw: " + err.message);
    }
  }, [withdraw, showSuccess, showError]);

  const handleJoinWaitlist = useCallback(async (jobId) => {
    try {
      await joinWaitlist(jobId);
      showSuccess("Joined waitlist.");
    } catch (err) {
      showError("Failed to join waitlist: " + err.message);
    }
  }, [joinWaitlist, showSuccess, showError]);

  const handleLeaveWaitlist = useCallback(async (jobId) => {
    try {
      await leaveWaitlist(jobId);
      showSuccess("Left waitlist.");
    } catch (err) {
      showError("Failed to leave waitlist: " + err.message);
    }
  }, [leaveWaitlist, showSuccess, showError]);

  return (
    <div className="page-container">
      <JobPage
        jobs={jobs}
        currentUser={currentUser}
        loading={jobsLoading}
        onSignup={handleSignup}
        onWithdraw={handleWithdraw}
        onJoinWaitlist={handleJoinWaitlist}
        onLeaveWaitlist={handleLeaveWaitlist}
      />
    </div>
  );
}

export function ProfilePage({ currentUser }) {
  const { showSuccess, showError, addEntry, updateProfile, updateAuthEmail, changePassword } = useAppContext();

  const handleSave = useCallback(async (updates) => {
    try {
      const trackFields = ["name", "badge", "phone", "email"];
      const changes = {};
      for (const key of trackFields) {
        const oldVal = currentUser[key] ?? "";
        const newVal = updates[key] ?? "";
        if (String(oldVal) !== String(newVal)) {
          changes[key] = { from: oldVal, to: newVal };
        }
      }
      const changedFields = Object.keys(changes);
      const detail = changedFields.length > 0 ? `Updated profile: ${changedFields.join(", ")}` : "Updated own profile";
      await updateProfile(currentUser.id, updates);
      await addEntry(currentUser, "profile_updated", null, null, detail, null, null, changedFields.length > 0 ? changes : null);
      showSuccess("Profile updated.");
    } catch (err) {
      showError("Failed to update profile: " + err.message);
    }
  }, [updateProfile, addEntry, currentUser, showSuccess, showError]);

  const handleUpdateDocuments = useCallback(async (documents) => {
    try {
      await updateProfile(currentUser.id, { documents });
    } catch (err) {
      showError("Failed to update documents: " + err.message);
      throw err;
    }
  }, [updateProfile, currentUser.id, showError]);

  return (
    <div className="page-container">
      <ProfileEditor
        currentUser={currentUser}
        onSave={handleSave}
        onUpdateEmail={updateAuthEmail}
        onChangePassword={changePassword}
        onUpdateDocuments={handleUpdateDocuments}
      />
    </div>
  );
}

export function ReportsPage({ jobs, users }) {
  return (
    <div className="page-container">
      <ReportingDashboard jobs={jobs} users={users} />
    </div>
  );
}

export function AttendancePage({ job, users, onDone }) {
  const { currentUser, showSuccess, showError, addEntry, recordAttendance } = useAppContext();

  const handleSave = useCallback(async (jobId, att, completeJob) => {
    try {
      await recordAttendance(jobId, att, completeJob);
      const action = completeJob ? "Attendance recorded and job completed" : "Attendance draft saved";
      await addEntry(currentUser, "attendance_recorded", jobId, job?.title,
        `${action} for ${Object.keys(att).length} officers`);
      showSuccess(completeJob ? "Attendance saved. Job marked as completed." : "Attendance draft saved.");
      onDone();
    } catch (err) {
      showError("Failed to save attendance: " + err.message);
      throw err;
    }
  }, [recordAttendance, addEntry, currentUser, job, showSuccess, showError, onDone]);

  return (
    <div className="page-container">
      <AttendanceForm job={job} users={users} onSave={handleSave} onCancel={onDone} />
    </div>
  );
}
